import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	[
		"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all cursor-pointer",
		"disabled:pointer-events-none disabled:opacity-50",
		"[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
		"outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]",
		"aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
	],
	{
		variants: {
			variant: {
				primary:
					"bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
				secondary:
					"bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
				outline:
					"border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
				destructive:
					"bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
				input: [
					"flex h-9 w-full items-center justify-between whitespace-nowrap",
					"border rounded-none border-input px-3 py-2 text-sm data-[placeholder]:text-muted-foreground [&>span]:line-clamp-1",
					"focus-visible:outline-none focus-visible:border-input-focus focus-visible:inset-ring-1 focus-visible:inset-ring-input-focus", // On focus, change border color and add inset ring
					"disabled:cursor-not-allowed disabled:opacity-50",
				],
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
				lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
				icon: "size-9",
			},
		},
		defaultVariants: {
			variant: "primary",
			size: "default",
		},
	},
);

type ButtonProps = React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	};
function Button(props: ButtonProps) {
	const { className, variant, size, asChild = false, ...otherProps } = props;
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...otherProps}
		/>
	);
}

export { Button, buttonVariants };
